import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { DocIcon } from '../DocIcon'
import { Icon } from '../Icon'
import type { PlanHuntDocItem } from './types'

export interface PlanHuntDocAttachmentRowProps {
  doc: PlanHuntDocItem
  source: 'team' | 'device'
  onRemove: () => void
  /**
   * Optional preview handler. When provided (and the doc is available), the
   * icon + name become a clickable control that opens a preview — matching
   * the view-hunt-plan behaviour where attachments can be opened.
   */
  onView?: () => void
  last?: boolean
  unavailable?: boolean
}

export function PlanHuntDocAttachmentRow({
  doc,
  source,
  onRemove,
  onView,
  last = false,
  unavailable = false,
}: PlanHuntDocAttachmentRowProps) {
  const { t } = useTranslation('planHunt')
  const subtitle = [doc.size, source === 'team' ? t('docAttachmentRow.fromTeam') : t('docAttachmentRow.fromDevice')]
    .filter(Boolean)
    .join(' · ')
  const clickable = !!onView && !unavailable
  const body = (
    <>
      <DocIcon kind={doc.kind} size={32} />
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {unavailable ? t('docAttachmentRow.unavailable') : doc.name}
        </div>
        <div style={{ fontSize: 11, color: COLORS.outline }}>{subtitle}</div>
      </div>
    </>
  )
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        borderBottom: last ? 'none' : `1px solid ${COLORS.surfaceContainerHighest}`,
        opacity: unavailable ? 0.6 : 1,
      }}
    >
      {clickable ? (
        <button
          type="button"
          onClick={onView}
          aria-label={t('docAttachmentRow.preview', { name: doc.name })}
          title={t('docAttachmentRow.preview', { name: doc.name })}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: 'none',
            border: 'none',
            padding: 0,
            margin: 0,
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          {body}
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>{body}</div>
      )}
      <button
        type="button"
        className="wh-btn-icon"
        onClick={onRemove}
        aria-label={t('docAttachmentRow.remove', { name: doc.name })}
        title={t('docAttachmentRow.removeAttachment')}
      >
        <Icon name="close" size={16} />
      </button>
    </div>
  )
}
